/**
 * Shared geo-level gating — single source of truth for:
 *   - which levels each theme allows (ALLOWED_LEVELS)
 *   - which of those levels are selectable given current state context (getSelectableLevels)
 *   - whether a theme enables national state-leg display (allowNationalStatelegForTheme)
 *
 * Previously: ALLOWED_LEVELS was duplicated three times inside MapPage.js;
 * the allowNationalStateleg expression and the MapToolbar inline visibility
 * predicates were separate. All now derived from this file.
 */

// Verbatim copy of the MapPage constant — all 8 theme entries, order preserved.
export const ALLOWED_LEVELS = {
  default:           ["states", "counties", "cd", "zipcodes", "media_market"],
  phrma:             ["states", "statesenate", "statehouse"],
  invariant:         ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  invariant_industry:["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  invariant_message: ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  invariant_h2h:     ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  example_client:    ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  stateleg:          ["states", "statesenate", "statehouse"],
  // Tracking Questions (Top Issues, economic approval, immigration and the rest
  // of polAggregators) used to fall through to `default`, which has no
  // state-legislative levels, so the tracking batteries were the only ones in
  // the app that could not be read at state-leg despite being modelled there.
  //
  // Verified before enabling, on the latest declared wave of all 14 pol groups:
  // every one returns 1,950 upper-chamber and 4,890 lower-chamber districts,
  // each summing to 100. These are real layers, not empty tabs.
  //
  // allowNationalStatelegForTheme is false for `pol`, so the two tabs only
  // appear once a state is selected. That is deliberate: 4,890 house districts
  // nationally is not a map anyone reads.
  pol:               ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  // Prediction Markets, same story. This theme spans TWO schemas: the three
  // behavioural questions are `single` and the three prediction-market ones are
  // `tracking`, so both had to be checked separately before opening the levels.
  //
  // Verified: all six groups return 1,950 upper and 4,890 lower districts
  // summing to 100, and for the tracked ones that holds on every declared wave
  // (regulation on 3 and 4, econ_approve on 1 through 4).
  gamble:            ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  // AVIA works at state-legislative level, and the tracking schema carries both
  // self-driving batteries in stateupper/statelower for waves 1-4, so the state
  // senate and house levels are real here rather than empty tabs.
  selfdrive:         ["states", "counties", "cd", "zipcodes", "statesenate", "statehouse", "media_market"],
  // The AI-boom reel composes tracking and ACC variables in one theme, so its
  // allowed levels are the INTERSECTION of what both schemas serve. Tracking
  // reaches zipcode and media market; ACC does not, so those are excluded or a
  // data-centre slide would offer levels that come back empty.
  aiboom:            ["states", "counties", "cd"],
  // ACC data is not available at zipcode or media-market level.
  acc_politics:      ["states", "counties", "cd"],
  acc_policy:        ["states", "counties", "cd"],
  acc_climate:       ["states", "counties", "cd"],
};

/**
 * Returns true when statesenate/statehouse buttons should be shown nationally
 * (i.e. without requiring a state to be selected first).
 *
 * Verbatim encoding of the old MapPage:3218 expression:
 *   String(selectedSection || "").startsWith("invariant") || selectedSection === "stateleg"
 *
 * @param {string|null|undefined} theme  The selectedSection slug.
 * @returns {boolean}
 */
export function allowNationalStatelegForTheme(theme) {
  return String(theme || "").startsWith("invariant") || theme === "stateleg";
}

/**
 * Returns the list of levels currently selectable in the toolbar/selector
 * for the given theme and state-context combination.
 *
 * Implements the MapToolbar visibility predicates verbatim:
 *   states / cd / counties / media_market → always shown if in allowed set
 *   statesenate / statehouse              → also requires hasStateContext OR allowNationalStateleg
 *   zipcodes                              → also requires hasStateContext (no allowNationalStateleg fallback)
 *
 * @param {string|null|undefined} theme                 selectedSection slug.
 * @param {boolean}               hasStateContext        true when selectedStateCode is set.
 * @param {boolean}               [allowNational]        allowNationalStatelegForTheme result; pass false
 *                                                       if you want ONLY the state-gated behaviour.
 * @returns {string[]}  Ordered list of selectable level strings.
 */
export function getSelectableLevels(theme, hasStateContext, allowNational = false) {
  const allowed = ALLOWED_LEVELS[theme] || ALLOWED_LEVELS.default;
  return allowed.filter(level => {
    if (level === "zipcodes")                              return hasStateContext;
    if (level === "statesenate" || level === "statehouse") return hasStateContext || allowNational;
    return true;
  });
}
